import { SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { Lead, loadLeads, STORAGE_KEY } from './leads';
import { workflowApi } from './workflows';

type StoredLead = Lead & { revision?: number };
const content = (lead: StoredLead) => JSON.stringify({ ...lead, revision: undefined });
export function useCRMLeads() {
  const [leads, state] = useState<Lead[]>([]); const current = useRef<Lead[]>([]);
  const [ready, setReady] = useState(false); const [online, setOnline] = useState(false);
  const [storageBlocked, setStorageBlocked] = useState(false); const [syncError, setSyncError] = useState(''); const [pendingCount, count] = useState(0);
  const versions = useRef(new Map<string, number>()); const pending = useRef(new Map<string, Lead | null>());
  const working = useRef(false); const alive = useRef(true); const enabled = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(); const flushRef = useRef<() => Promise<void>>();
  const display = useCallback((next: Lead[]) => { current.current = next; if (alive.current) state(next); }, []);
  const flush = useCallback(async () => {
    if (!enabled.current || working.current) return;
    working.current = true;
    try {
      while (pending.current.size) {
        const [id, value] = pending.current.entries().next().value as [string, Lead | null];
        if (value) {
          const saved = await workflowApi<StoredLead>('/leads/save', { lead: value, revision: versions.current.get(id) || 0 });
          versions.current.set(id, saved.revision || 1);
          if (pending.current.get(id) === value) { pending.current.delete(id); display(current.current.map(l => l.id === id ? saved : l)); }
        } else {
          if (versions.current.has(id)) await workflowApi('/leads/delete', { id, revision: versions.current.get(id) });
          pending.current.delete(id); versions.current.delete(id);
        }
        if (alive.current) count(pending.current.size);
      }
      if (alive.current) setSyncError('');
    } catch (e) { if (alive.current) setSyncError((e as Error).message); }
    finally { working.current = false; }
  }, [display]);
  flushRef.current = flush;
  const setLeads = useCallback((action: SetStateAction<Lead[]>) => {
    const previous = current.current; const next = typeof action === 'function' ? action(previous) : action;
    for (const l of next) { const old = previous.find(p => p.id === l.id); if (!old || content(old) !== content(l)) pending.current.set(l.id, l); }
    for (const l of previous) if (!next.some(n => n.id === l.id)) pending.current.set(l.id, null);
    display(next); count(pending.current.size); clearTimeout(timer.current);
    timer.current = setTimeout(() => flushRef.current?.(), 600);
  }, [display]);
  const refresh = useCallback(async () => {
    if (!enabled.current || working.current) return;
    const rows = await workflowApi<StoredLead[]>('/leads');
    if (working.current) return;
    const fresh = rows.filter(l => !pending.current.has(l.id)).map(row => (row.revision || 1) < (versions.current.get(row.id) || 0) ? current.current.find(l => l.id === row.id) || row : row);
    for (const row of rows) if (!pending.current.has(row.id)) versions.current.set(row.id, Math.max(versions.current.get(row.id) || 0, row.revision || 1));
    display([...current.current.filter(l => pending.current.get(l.id)), ...fresh]);
  }, [display]);
  useEffect(() => {
    alive.current = true; let local: Lead[] = [];
    try { local = loadLeads(); display(local); } catch (e) { setSyncError((e as Error).message); setStorageBlocked(true); }
    async function init() {
      try {
        const health = await workflowApi<{ crm?: boolean }>('/health');
        if (!health.crm || !alive.current) return;
        const response = local.length ? await workflowApi<{ leads: StoredLead[] }>('/leads/import', { leads: local }) : { leads: await workflowApi<StoredLead[]>('/leads') };
        if (!alive.current) return;
        for (const row of response.leads) versions.current.set(row.id, row.revision || 1);
        enabled.current = true; setOnline(true); display(response.leads);
      } catch (e) { if (alive.current) setSyncError(`Dados locais preservados. ${(e as Error).message}`); }
      finally { if (alive.current) setReady(true); }
    }
    init(); const poll = setInterval(() => { refresh().catch(e => { if (alive.current) setSyncError((e as Error).message); }); }, 5000);
    return () => { alive.current = false; enabled.current = false; clearInterval(poll); clearTimeout(timer.current); };
  }, [display, refresh]);
  useEffect(() => {
    if (!ready || storageBlocked) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(leads)); } catch { setSyncError('Backup local indisponível. Baixe o backup antes de fechar.'); }
  }, [leads, ready, storageBlocked]);
  useEffect(() => {
    const unload = (event: BeforeUnloadEvent) => { if (pending.current.size) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', unload); return () => window.removeEventListener('beforeunload', unload);
  }, []);
  return { leads, setLeads, ready, online, storageBlocked, setStorageBlocked, syncError, pendingCount, flush, refresh };
}
